/**
 * How two parts meet: the volume they share, the faces they share, and the
 * gap between them. Plain numbers, no Babylon; both parts in the same frame.
 */

import { fitPart, sizeOf, type FitPart } from './parts';
import { FACE_STRIDE, placeShape, solidContains, type FitSolid, type Vec3 } from './solids';

export interface FitOverlap {
    /**
     * How far one part is sunk into the other: the smallest extent, along `axis`, of the volume one solid of
     * each shares; of the deepest such pair not there by design, else of the deepest pair.
     */
    readonly depth: number;
    readonly axis: 0 | 1 | 2;
    /** That volume's extent along each frame axis. */
    readonly extent: Vec3;
    readonly volume: number;
    readonly centre: Vec3;
    /**
     * Why the overlap is there by design, or null: `stud`, knobs on a part's top seated in the part above,
     * no deeper than they stand out; `embedded`, one part held wholly inside a bigger one (a rim in a tyre);
     * `insertion`, a knob on another side that is round, thin or on a rod (a pin with its collar in a hub, a
     * clip, a claw on a rod), a slender piece filling no more than its own cross-section that is round or
     * runs right through the other (an axle in a hole, a bolt through a hole the model leaves out, a bar through
     * a clip), a round end inside a wider round piece on its axis (a hose end screwed into its fitting, an O-ring
     * in a cap), a thin arm wholly across a round rod, or a rod's end held in a part that is no rod (between a
     * claw's prongs); `surface`, a long, thin part lying along the other, not sunk its whole width (a drawn
     * line, a glue bead, a wire). Pieces at an angle to the frame are judged along their own axes too. Two
     * copies in one place are null.
     */
    readonly engagement: 'stud' | 'embedded' | 'insertion' | 'surface' | null;
}

export interface FitContact {
    readonly overlap: FitOverlap | null;
    /** Area of coplanar faces of the two that face the same way, knob caps aside: both fill that plane, so it flickers (z-fights). */
    readonly flush: number;
    /** Where the largest flush face pair lies: a point on it and its normal. Null with no flush faces. */
    readonly flushPlane: { readonly at: Vec3; readonly normal: Vec3 } | null;
    /** Area of coplanar faces that face each other: the parts rest face to face. */
    readonly touch: number;
    /** The part of `touch` across either part's up axis: one stacked on the other. */
    readonly touchUp: number;
    /** The distance between the two surfaces, 0 when they touch or overlap, Infinity beyond `reach`. */
    readonly gap: number;
}

export interface ContactOptions {
    /** Sampling step for the shared volume: depths are good to about one step. */
    readonly step: number;
    /** How far apart two parts may be and still have their gap measured. */
    readonly reach: number;
}

const COARSE = 16, FINE = 32;

/**
 * The volume two parts share, judged per pair of solids: each pair is sampled on a grid in the overlap of
 * the two solids' boxes, coarse then fine around what the coarse pass found, at most 32 cells a side. Null
 * when they share none. Depths are good to about one step, or to 1/32 of that box when that is coarser.
 */
export function overlap(a: FitPart, b: FitPart, step: number): FitOverlap | null {
    if (!boxesMeet(a.lo, a.hi, b.lo, b.hi)) return null;
    const tiny = 1e-9 * Math.max(sizeOf(a.lo, a.hi), sizeOf(b.lo, b.hi));
    const all = group();
    const judged: FitOverlap[] = [];
    let cell: Vec3 = [step, step, step];
    for (const sa of a.solids) {
        if (!sa.shape.closed || !boxesMeet(sa.lo, sa.hi, b.lo, b.hi)) continue;
        for (const sb of b.solids) {
            if (!sb.shape.closed) continue;
            const lo = [0, 1, 2].map(k => Math.max(sa.lo[k], sb.lo[k])) as Vec3, hi = [0, 1, 2].map(k => Math.min(sa.hi[k], sb.hi[k])) as Vec3;
            if (![0, 1, 2].every(k => hi[k] - lo[k] > tiny)) continue;
            const g = samplePair(sa, sb, { lo, hi }, step);
            if (!g) continue;
            cell = g.cell;
            for (let c = 0; c < 3; c++) { all.min[c] = Math.min(all.min[c], g.min[c]); all.max[c] = Math.max(all.max[c], g.max[c]); all.sum[c] += g.sum[c]; }
            all.count += g.count;
            const o = shape(g, null);
            judged.push({ ...o, engagement: judge(a, sa, b, sb, o.extent, g.cell) ?? judgeTurned(a, sa, b, sb, step) });
        }
    }
    if (!judged.length) return null;
    const whole = shape({ ...all, cell }, null);
    if (embedded(a, b, whole.extent)) return { ...whole, engagement: 'embedded' };
    judged.sort((x, y) => y.depth - x.depth);
    const worst = judged.find(o => !o.engagement);
    if (worst && (rodEnd(a, b) || rodEnd(b, a))) return { ...worst, engagement: 'insertion' };
    return worst ?? judged[0];
}

/**
 * A rod's end held in a part that is no rod, however the two meet there (between a claw's prongs, in a hub): one
 * of the rod's ends, not both, has the other's material on both sides of it across one direction and none just
 * outside it across the direction square to that, within two radii of the end. A rod sunk along a face or a floor has material
 * under it, so it is not held.
 */
function rodEnd(rod: FitPart, other: FitPart): boolean {
    const r = rodOf(rod);
    if (!r || rodOf(other)) return false;
    const cross = [0, 1, 2].filter(k => k !== r.along);
    const solid = (p: number[]): boolean => {
        const q = r.m ? [0, 1, 2].map(i => p[0] * r.m![i * 4] + p[1] * r.m![i * 4 + 1] + p[2] * r.m![i * 4 + 2]) : p;
        return other.solids.some(s => solidContains(s, q[0], q[1], q[2]));
    };
    const at = (along: number, angle: number, offset: number): number[] => {
        const p = [...r.centre];
        p[r.along] = along;
        p[cross[0]] += Math.cos(angle) * offset;
        p[cross[1]] += Math.sin(angle) * offset;
        return p;
    };
    // Flanked across one direction (prongs either side) and open across the one square to it on both sides, at some
    // turn about the rod: gripped, not sunk in a face.
    const held = (end: number, inward: number): boolean => [0.25, 0.5, 1, 2].some(depth => {
        const along = end + inward * depth * r.radius;
        return Array.from({ length: 16 }, (_, i) => i * Math.PI / 16).some(angle => [-1, 1].every(side =>
            solid(at(along, angle, side * 0.8 * r.radius)) && !solid(at(along, angle + Math.PI / 2, side * 1.2 * r.radius))));
    });
    return held(r.ends[0], 1) !== held(r.ends[1], -1);
}

interface Rod { readonly m: number[] | null; readonly along: number; readonly ends: [number, number]; readonly centre: number[]; readonly radius: number }
const rods = new WeakMap<FitPart, Rod | null>();
/**
 * A part that is a rod: its biggest solid convex and round about its length, along its own axes, and the part at
 * least three times as long as it is wide. Its axis, ends, centre and radius are in the frame turned onto those axes
 * (`m`, row-vector layout, turns the frame onto them; null when they are the frame's).
 */
function rodOf(part: FitPart): Rod | null {
    if (rods.has(part)) return rods.get(part)!;
    const size = (x: FitSolid): number => (x.hi[0] - x.lo[0]) * (x.hi[1] - x.lo[1]) * (x.hi[2] - x.lo[2]);
    const body = part.solids.reduce((m, x) => (size(x) > size(m) ? x : m), part.solids[0]);
    let rod: Rod | null = null;
    if (body?.shape.convex) {
        const m = ownAxes(body), t = m ? turned(part, m) : part, s = t.solids[part.solids.indexOf(body)];
        const e = [0, 1, 2].map(k => t.hi[k] - t.lo[k]), along = e.indexOf(Math.max(...e));
        if (e[along] >= 3 * Math.max(...e.filter((_, k) => k !== along)) && s.hi[along] - s.lo[along] >= 0.5 * e[along] && roundAbout(s, along)) {
            const radius = Math.max(...[0, 1, 2].filter(k => k !== along).map(k => s.hi[k] - s.lo[k])) / 2;
            rod = { m, along, ends: [t.lo[along], t.hi[along]], centre: [0, 1, 2].map(k => (s.lo[k] + s.hi[k]) / 2), radius };
        }
    }
    rods.set(part, rod);
    return rod;
}

type Engagement = Exclude<FitOverlap['engagement'], 'embedded'>;

/** Why solid `sa` of `a` and `sb` of `b`, sharing a volume of `extent`, are fitted by design, judged along the frame's axes. */
function judge(a: FitPart, sa: FitSolid, b: FitPart, sb: FitSolid, extent: Vec3, cell: Vec3): Engagement {
    return inserted(a, sa, sb, extent, cell) ?? inserted(b, sb, sa, extent, cell)
        ?? (coaxial(sa, sb, extent, cell) || clipped(sa, sb, extent, cell) || clipped(sb, sa, extent, cell) ? 'insertion' : null);
}

/**
 * `judge` along the own axes of either solid when it stands at an angle to the frame (a rod or a claw on a
 * diagonal), where its frame box is no measure of it: both parts turned so those axes are the frame's.
 */
function judgeTurned(a: FitPart, sa: FitSolid, b: FitPart, sb: FitSolid, step: number): Engagement {
    for (const s of [sa, sb]) {
        const m = ownAxes(s);
        if (!m) continue;
        const ta = turned(a, m), tb = turned(b, m), ra = ta.solids[a.solids.indexOf(sa)], rb = tb.solids[b.solids.indexOf(sb)];
        const lo = [0, 1, 2].map(k => Math.max(ra.lo[k], rb.lo[k])) as Vec3, hi = [0, 1, 2].map(k => Math.min(ra.hi[k], rb.hi[k])) as Vec3;
        if (![0, 1, 2].every(k => hi[k] > lo[k])) continue;
        const g = samplePair(ra, rb, { lo, hi }, step);
        const found = g && judge(ta, ra, tb, rb, shape(g, null).extent, g.cell);
        if (found) return found;
    }
    return null;
}

const axesOf = new WeakMap<FitSolid, number[] | null>();
/**
 * The rotation (row-vector layout, 16 numbers) onto a convex solid's own axes, when its frame box is over half as
 * big again as its box along them; else null. The axes: the face direction with the most area, then the one across it with the most.
 */
function ownAxes(s: FitSolid): number[] | null {
    if (axesOf.has(s)) return axesOf.get(s)!;
    let m: number[] | null = null;
    if (s.shape.convex) {
        const byDir = new Map<string, { n: Vec3; area: number }>();
        for (let o = 0; o < s.faces.length; o += FACE_STRIDE) {
            if (!(s.faces[o + 4] > 0)) continue;
            const f = [s.faces[o], s.faces[o + 1], s.faces[o + 2]];
            const major = [0, 1, 2].reduce((x, k) => (Math.abs(f[k]) > Math.abs(f[x]) ? k : x), 0);
            const n = f.map(v => (f[major] < 0 ? -v : v)) as Vec3, key = n.map(v => Math.round(v * 200)).join(',');
            const d = byDir.get(key);
            if (d) d.area += s.faces[o + 4]; else byDir.set(key, { n, area: s.faces[o + 4] });
        }
        const dirs = [...byDir.values()].sort((x, y) => y.area - x.area);
        const dot = (u: Vec3, v: Vec3): number => u[0] * v[0] + u[1] * v[1] + u[2] * v[2];
        const n1 = dirs[0]?.n, n2 = dirs.find(d => Math.abs(dot(d.n, n1)) < 0.02)?.n;
        if (n1 && n2) {
            const n3: Vec3 = [n1[1] * n2[2] - n1[2] * n2[1], n1[2] * n2[0] - n1[0] * n2[2], n1[0] * n2[1] - n1[1] * n2[0]];
            const l3 = Math.hypot(...n3), w = n3.map(v => v / l3) as Vec3;
            const v: Vec3 = [w[1] * n1[2] - w[2] * n1[1], w[2] * n1[0] - w[0] * n1[2], w[0] * n1[1] - w[1] * n1[0]];
            const axes = [n1, v, w];
            let own = 1;
            for (const u of axes) {
                let lo = Infinity, hi = -Infinity;
                for (let i = 0; i < s.positions.length; i += 3) { const x = s.positions[i] * u[0] + s.positions[i + 1] * u[1] + s.positions[i + 2] * u[2]; lo = Math.min(lo, x); hi = Math.max(hi, x); }
                own *= hi - lo;
            }
            const box = (s.hi[0] - s.lo[0]) * (s.hi[1] - s.lo[1]) * (s.hi[2] - s.lo[2]);
            if (box > 1.5 * own) m = [n1[0], v[0], w[0], 0, n1[1], v[1], w[1], 0, n1[2], v[2], w[2], 0, 0, 0, 0, 1];
        }
    }
    axesOf.set(s, m);
    return m;
}

const turnedParts = new WeakMap<FitPart, Map<string, FitPart>>();
/** A part turned by rotation `m` (row-vector layout). */
function turned(part: FitPart, m: number[]): FitPart {
    let byM = turnedParts.get(part);
    if (!byM) turnedParts.set(part, byM = new Map<string, FitPart>());
    const key = m.join(',');
    let t = byM.get(key);
    if (!t) {
        const solids = part.solids.map(s => placeShape({ ...s.shape, positions: s.positions }, m));
        const u = part.up;
        t = fitPart(solids, [0, 1, 2].map(j => u[0] * m[j] + u[1] * m[4 + j] + u[2] * m[8 + j]) as Vec3);
        byM.set(key, t);
    }
    return t;
}

function shape(g: Group & { cell: Vec3 }, engagement: FitOverlap['engagement']): FitOverlap {
    const extent = g.min.map((v, k) => g.max[k] - v + g.cell[k]) as Vec3;
    const axis: 0 | 1 | 2 = extent[0] <= extent[1] && extent[0] <= extent[2] ? 0 : extent[1] <= extent[2] ? 1 : 2;
    return { depth: extent[axis], axis, extent, volume: g.count * g.cell[0] * g.cell[1] * g.cell[2], centre: g.sum.map(v => v / g.count) as Vec3, engagement };
}

/** The volume two solids share, coarse then fine; null when the samples find none. */
function samplePair(sa: FitSolid, sb: FitSolid, box: { lo: Vec3; hi: Vec3 }, step: number): (Group & { cell: Vec3 }) | null {
    const coarse = sampleBox(sa, sb, box, step * 3, COARSE);
    if (coarse.count) {
        box = { lo: coarse.min.map((v, k) => Math.max(box.lo[k], v - coarse.cell[k])) as Vec3, hi: coarse.max.map((v, k) => Math.min(box.hi[k], v + coarse.cell[k])) as Vec3 };
    } else if (coarse.cell.every(c => c <= step * 3 + 1e-12) && Math.min(box.hi[0] - box.lo[0], box.hi[1] - box.lo[1], box.hi[2] - box.lo[2]) > step * 3) {
        return null;
    }
    const fine = sampleBox(sa, sb, box, step, FINE);
    return fine.count ? fine : null;
}

interface Group { count: number; min: Vec3; max: Vec3; sum: Vec3 }
const group = (): Group => ({ count: 0, min: [Infinity, Infinity, Infinity], max: [-Infinity, -Infinity, -Infinity], sum: [0, 0, 0] });

/** One part held wholly inside a bigger one (a rim in a tyre); two copies in one place are not. */
function embedded(a: FitPart, b: FitPart, extent: Vec3): boolean {
    const ea = a.hi.map((v, k) => v - a.lo[k]), eb = b.hi.map((v, k) => v - b.lo[k]);
    const [small, large] = ea[0] * ea[1] * ea[2] <= eb[0] * eb[1] * eb[2] ? [ea, eb] : [eb, ea];
    const duplicate = [0, 1, 2].every(k => large[k] <= 1.15 * small[k]);
    return !duplicate && [0, 1, 2].every(k => extent[k] >= 0.85 * small[k]);
}

/**
 * How solid `s` of `part` is in solid `other` by design, given the extent of what they share, else null:
 * a knob no deeper than it stands out (`stud` on the part's top; `insertion` elsewhere when round, thin or on
 * a rod: a pin in a hub, a clip); filling no more than its own cross-section, a slender piece that is
 * round or runs right through `other`, or the round end of a long, narrow part (`insertion`: a bolt through a
 * hole the model leaves out, a rod end in a connector); or a long, thin part lying along the other, not sunk its
 * whole width (`surface`).
 */
function inserted(part: FitPart, s: FitSolid, other: FitSolid, extent: Vec3, cell: Vec3): 'stud' | 'insertion' | 'surface' | null {
    const up = (k: FitPart['knobs'][number]): boolean => Math.sign(part.up[k.axis]) === k.sign && Math.abs(part.up[k.axis]) > 0.99;
    // A piece can stand out of two faces (a low stud beside a riser); its top one makes it a stud.
    const knob = part.knobs.find(k => k.solid === s && up(k)) ?? part.knobs.find(k => k.solid === s);
    // Off the top, a knob is fitted when it is round (a pin, an axle), a thin arm (a clip) or on a rod (a claw on
    // a rod); a leg or an arm standing out of a body is not.
    if (knob && extent[knob.axis] <= knob.height * 1.1 + cell[knob.axis] && (up(knob) || fitsAsKnob(s, knob.axis, other))) return up(knob) ? 'stud' : 'insertion';
    const e = [0, 1, 2].map(k => s.hi[k] - s.lo[k]);
    const fills = (ks: number[]): boolean => ks.every(k => extent[k] >= 0.6 * e[k] && extent[k] <= 1.1 * e[k] + cell[k]);
    // The round end piece of a long, narrow part (a rod, a hose, an axle), pushed into the other part.
    const p = [0, 1, 2].map(k => part.hi[k] - part.lo[k]), along = p.indexOf(Math.max(...p)), cross = [0, 1, 2].filter(k => k !== along);
    const ends = s.lo[along] <= part.lo[along] + cell[along] || s.hi[along] >= part.hi[along] - cell[along];
    if (ends && p[along] >= 2 * Math.max(...cross.map(k => p[k])) && roundAbout(s, along) && fills(cross)) return 'insertion';
    if (p[along] >= 8 * Math.max(...cross.map(k => p[k])) && extent[along] >= 3 * Math.max(...cross.map(k => extent[k]))
        && cross.some(k => extent[k] <= 0.9 * p[k] - cell[k])) return 'surface';
    const long = e.indexOf(Math.max(...e));
    const across = [0, 1, 2].filter(k => k !== long);
    const wide = Math.max(e[across[0]], e[across[1]]), thin = Math.min(e[across[0]], e[across[1]]);
    // Round, or square but running right through the other (a bar through a clip): an arm buried in a block is not.
    if (e[long] < 2 * wide || wide > 2.5 * thin || !(roundAbout(s, long) || (s.lo[long] < other.lo[long] && s.hi[long] > other.hi[long]))) return null;
    return fills(across) ? 'insertion' : null;
}

/**
 * A knob off a part's top that is fitted: round about its axis (a pin, an axle), a thin arm (a clip) or on a rod
 * (a claw: the other piece round about an axis it is at least twice as long along as it is wide).
 */
function fitsAsKnob(s: FitSolid, axis: number, other: FitSolid): boolean {
    const sorted = [0, 1, 2].map(k => s.hi[k] - s.lo[k]).sort((x, y) => x - y);
    const e = [0, 1, 2].map(k => other.hi[k] - other.lo[k]);
    return roundAbout(s, axis) || sorted[0] <= sorted[1] / 3
        || [0, 1, 2].some(k => roundAbout(other, k) && e[k] >= 2 * Math.max(...e.filter((_, x) => x !== k)));
}

/** Whether a solid is round about frame axis `k`: its box is square across it, and its faces look out in at least 6 directions across it. */
const rounds = new WeakMap<FitSolid, boolean[]>();
function roundAbout(s: FitSolid, k: number): boolean {
    let known = rounds.get(s);
    if (!known) {
        known = [0, 1, 2].map(axis => {
            const [i, j] = [0, 1, 2].filter(x => x !== axis), wi = s.hi[i] - s.lo[i], wj = s.hi[j] - s.lo[j];
            if (Math.min(wi, wj) < 0.85 * Math.max(wi, wj)) return false;
            const sides = new Set<number>();
            for (let o = 0; o < s.faces.length; o += FACE_STRIDE) {
                if (s.faces[o + 4] > 0 && Math.abs(s.faces[o + axis]) < 0.2) sides.add(Math.round(Math.atan2(s.faces[o + j], s.faces[o + i]) * 18 / Math.PI));
            }
            return sides.size >= 6;
        });
        rounds.set(s, known);
    }
    return known[k];
}

/**
 * Two solids round about one axis, the narrower inside the wider across its whole cross-section, and along the
 * axis too unless it is at least as long as it is wide: a round end screwed or pushed into a round socket the
 * model leaves out (a hose into a spray head, an O-ring in a cap). Two discs stacked on the axis are not.
 */
function coaxial(sa: FitSolid, sb: FitSolid, extent: Vec3, cell: Vec3): boolean {
    return [0, 1, 2].some(k => {
        if (!roundAbout(sa, k) || !roundAbout(sb, k)) return false;
        const across = [0, 1, 2].filter(x => x !== k);
        const width = (s: FitSolid): number => Math.max(s.hi[across[0]] - s.lo[across[0]], s.hi[across[1]] - s.lo[across[1]]);
        const [narrow, wide] = width(sa) <= width(sb) ? [sa, sb] : [sb, sa];
        const w = width(narrow);
        const fill = (x: number): boolean => extent[x] >= 0.85 * (narrow.hi[x] - narrow.lo[x]) - cell[x];
        return w <= 0.9 * width(wide) && (narrow.hi[k] - narrow.lo[k] >= w || fill(k))
            && across.every(x => Math.abs((narrow.lo[x] + narrow.hi[x]) - (wide.lo[x] + wide.hi[x])) / 2 <= 0.1 * w && fill(x));
    });
}

/**
 * A thin arm of one part (a claw, a clip) across a round piece of the other (a rod, its end), wholly inside it: the
 * arm no wider than the rod, and a third of its thickness at most.
 */
function clipped(arm: FitSolid, rod: FitSolid, extent: Vec3, cell: Vec3): boolean {
    const e = [0, 1, 2].map(k => arm.hi[k] - arm.lo[k]), sorted = [...e].sort((x, y) => x - y);
    const thin = e.indexOf(sorted[0]);
    return sorted[0] <= sorted[1] / 3 && extent[thin] >= 0.85 * e[thin] - cell[thin]
        && [0, 1, 2].some(k => {
            const width = Math.min(...[0, 1, 2].filter(x => x !== k).map(x => rod.hi[x] - rod.lo[x]));
            return k !== thin && roundAbout(rod, k) && 3 * sorted[0] <= width && sorted[1] <= width;
        });
}

function boxesMeet(alo: Vec3, ahi: Vec3, blo: Vec3, bhi: Vec3, pad = 0): boolean {
    return alo[0] <= bhi[0] + pad && ahi[0] >= blo[0] - pad && alo[1] <= bhi[1] + pad && ahi[1] >= blo[1] - pad && alo[2] <= bhi[2] + pad && ahi[2] >= blo[2] - pad;
}

function sampleBox(sa: FitSolid, sb: FitSolid, box: { lo: Vec3; hi: Vec3 }, step: number, most: number): Group & { cell: Vec3 } {
    const n = [0, 1, 2].map(k => Math.min(most, Math.max(1, Math.ceil((box.hi[k] - box.lo[k]) / step))));
    const cell = [0, 1, 2].map(k => (box.hi[k] - box.lo[k]) / n[k]) as Vec3;
    const g = { ...group(), cell };
    for (let i = 0; i < n[0]; i++) {
        const x = box.lo[0] + (i + 0.5) * cell[0];
        for (let j = 0; j < n[1]; j++) {
            const y = box.lo[1] + (j + 0.5) * cell[1];
            for (let k = 0; k < n[2]; k++) {
                const z = box.lo[2] + (k + 0.5) * cell[2];
                if (!solidContains(sa, x, y, z) || !solidContains(sb, x, y, z)) continue;
                g.count++;
                const p = [x, y, z];
                for (let c = 0; c < 3; c++) { g.min[c] = Math.min(g.min[c], p[c]); g.max[c] = Math.max(g.max[c], p[c]); g.sum[c] += p[c]; }
            }
        }
    }
    return g;
}

/** The volume, faces and gap two parts share. */
export function contact(a: FitPart, b: FitPart, options: ContactOptions): FitContact {
    const shared = overlap(a, b, options.step);
    const faces = boxesMeet(a.lo, a.hi, b.lo, b.hi, faceEps(a, b)) ? coplanar(a, b) : { flush: 0, flushPlane: null, touch: 0, touchUp: 0 };
    const gap = shared || faces.touch > 0 || faces.flush > 0 ? 0 : distance(a, b, options.reach);
    return { overlap: shared, ...faces, gap };
}

const faceEps = (a: FitPart, b: FitPart): number => 1e-4 * Math.min(sizeOf(a.lo, a.hi), sizeOf(b.lo, b.hi));

interface Face { s: FitSolid; o: number; key: string; d: number; sign: number }

/** Faces of the part that touch the box, keyed by their plane's direction with the sign folded out. */
function facesIn(part: FitPart, lo: Vec3, hi: Vec3, skip: ReadonlySet<string> | null): Face[] {
    const out: Face[] = [];
    part.solids.forEach((s, si) => {
        const f = s.faces, p = s.positions, tri = s.shape.triangles;
        for (let o = 0, t = 0; o < f.length; o += FACE_STRIDE, t += 3) {
            if (!(f[o + 4] > 0) || skip?.has(`${si}:${o}`)) continue;
            let inside = true;
            for (let k = 0; k < 3 && inside; k++) {
                const a = p[tri[t] * 3 + k], b = p[tri[t + 1] * 3 + k], c = p[tri[t + 2] * 3 + k];
                inside = Math.max(a, b, c) >= lo[k] && Math.min(a, b, c) <= hi[k];
            }
            if (!inside) continue;
            const n = [f[o], f[o + 1], f[o + 2]];
            const major = [0, 1, 2].reduce((m, k) => (Math.abs(n[k]) > Math.abs(n[m]) ? k : m), 0);
            const sign = n[major] < 0 ? -1 : 1;
            out.push({ s, o, sign, d: sign * f[o + 3], key: n.map(v => Math.round(v * sign * 200)).join(',') });
        }
    });
    return out;
}

/** Faces a part's own pieces glue together (a stud's base cap on the body's top): hidden, so never flush. */
const glued = new WeakMap<FitPart, Set<string>>();
function gluedFaces(part: FitPart): Set<string> {
    let set = glued.get(part);
    if (set) return set;
    set = new Set();
    if (part.solids.length > 1) {
        const faces = facesIn(part, part.lo, part.hi, null);
        const eps = 1e-4 * sizeOf(part.lo, part.hi);
        const buckets = bucket(faces);
        const index = new Map(part.solids.map((s, i) => [s, i]));
        for (const list of buckets.values()) {
            for (const x of list) {
                for (const y of list) {
                    if (x.s === y.s || x.sign === y.sign || Math.abs(x.d - y.d) > eps) continue;
                    if (clipArea(x, y) >= 0.5 * x.s.faces[x.o + 4]) { set.add(`${index.get(x.s)}:${x.o}`); break; }
                }
            }
        }
    }
    glued.set(part, set);
    return set;
}

function bucket(faces: Face[]): Map<string, Face[]> {
    const m = new Map<string, Face[]>();
    for (const f of faces) { const l = m.get(f.key); if (l) l.push(f); else m.set(f.key, [f]); }
    return m;
}

function coplanar(a: FitPart, b: FitPart) {
    const eps = faceEps(a, b);
    const lo = a.lo.map((v, k) => Math.max(v, b.lo[k]) - eps) as Vec3, hi = a.hi.map((v, k) => Math.min(v, b.hi[k]) + eps) as Vec3;
    const fb = bucket(facesIn(b, lo, hi, gluedFaces(b)));
    let flush = 0, touch = 0, touchUp = 0, largest = 0, flushPlane: FitContact['flushPlane'] = null;
    for (const x of facesIn(a, lo, hi, gluedFaces(a))) {
        for (const y of fb.get(x.key) ?? []) {
            if (Math.abs(x.d - y.d) > eps) continue;
            const area = clipArea(x, y);
            if (!(area > 0)) continue;
            if (x.sign === y.sign) {
                // A knob's caps lie in the planes a seated part rests in, hidden under it: not a flicker.
                if (a.knobs.some(k => k.solid === x.s) || b.knobs.some(k => k.solid === y.s)) continue;
                flush += area;
                if (area > largest) { largest = area; flushPlane = { at: centroid(x), normal: [x.s.faces[x.o], x.s.faces[x.o + 1], x.s.faces[x.o + 2]] }; }
            } else {
                touch += area;
                const n = [x.s.faces[x.o], x.s.faces[x.o + 1], x.s.faces[x.o + 2]];
                if (Math.abs(n[0] * a.up[0] + n[1] * a.up[1] + n[2] * a.up[2]) > 0.9 || Math.abs(n[0] * b.up[0] + n[1] * b.up[1] + n[2] * b.up[2]) > 0.9) touchUp += area;
            }
        }
    }
    return { flush, flushPlane, touch, touchUp };
}

function corners(f: Face): Vec3[] {
    const tri = f.s.shape.triangles, p = f.s.positions, t = f.o / FACE_STRIDE * 3;
    return [0, 1, 2].map(j => [p[tri[t + j] * 3], p[tri[t + j] * 3 + 1], p[tri[t + j] * 3 + 2]] as Vec3);
}

function centroid(f: Face): Vec3 {
    const c = corners(f);
    return [0, 1, 2].map(k => (c[0][k] + c[1][k] + c[2][k]) / 3) as Vec3;
}

/** Area of the overlap of two coplanar triangles, in their plane. */
function clipArea(x: Face, y: Face): number {
    const n = [x.s.faces[x.o], x.s.faces[x.o + 1], x.s.faces[x.o + 2]];
    const ref = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    let u = [n[1] * ref[2] - n[2] * ref[1], n[2] * ref[0] - n[0] * ref[2], n[0] * ref[1] - n[1] * ref[0]];
    const ul = Math.hypot(u[0], u[1], u[2]);
    u = u.map(v => v / ul);
    const w = [n[1] * u[2] - n[2] * u[1], n[2] * u[0] - n[0] * u[2], n[0] * u[1] - n[1] * u[0]];
    const flat = (c: Vec3[]): [number, number][] => c.map(p => [p[0] * u[0] + p[1] * u[1] + p[2] * u[2], p[0] * w[0] + p[1] * w[1] + p[2] * w[2]]);
    return polygonArea(clip(flat(corners(x)), flat(corners(y))));
}

type P2 = [number, number];
const signedArea = (poly: P2[]): number => {
    let s = 0;
    for (let i = 0; i < poly.length; i++) { const a = poly[i], b = poly[(i + 1) % poly.length]; s += a[0] * b[1] - b[0] * a[1]; }
    return s / 2;
};
const polygonArea = (poly: P2[]): number => (poly.length < 3 ? 0 : Math.abs(signedArea(poly)));

/** Sutherland–Hodgman: the part of convex polygon `subject` inside convex polygon `window`. */
function clip(subject: P2[], window: P2[]): P2[] {
    const ccw = (poly: P2[]): P2[] => (signedArea(poly) < 0 ? [...poly].reverse() : poly);
    let out = ccw(subject);
    const win = ccw(window);
    for (let i = 0; i < win.length && out.length; i++) {
        const a = win[i], b = win[(i + 1) % win.length];
        const side = (p: P2): number => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
        const input = out;
        out = [];
        for (let j = 0; j < input.length; j++) {
            const cur = input[j], prev = input[(j + input.length - 1) % input.length];
            const cIn = side(cur) >= 0, pIn = side(prev) >= 0;
            if (cIn !== pIn) {
                const t = side(prev) / (side(prev) - side(cur));
                out.push([prev[0] + t * (cur[0] - prev[0]), prev[1] + t * (cur[1] - prev[1])]);
            }
            if (cIn) out.push(cur);
        }
    }
    return out;
}

/** The distance between the two parts' surfaces: vertex to surface either way, then edge to edge; Infinity beyond `reach`. */
function distance(a: FitPart, b: FitPart, reach: number): number {
    if (!boxesMeet(a.lo, a.hi, b.lo, b.hi, reach)) return Infinity;
    let best = Infinity;
    for (const [x, y] of [[a, b], [b, a]] as const) {
        for (const sy of y.solids) {
            if (!boxesMeet(sy.lo, sy.hi, x.lo, x.hi, reach)) continue;
            for (const sx of x.solids) {
                if (!boxesMeet(sx.lo, sx.hi, sy.lo, sy.hi, reach)) continue;
                const p = sx.positions;
                for (let i = 0; i < p.length; i += 3) {
                    if (p[i] < sy.lo[0] - reach || p[i] > sy.hi[0] + reach || p[i + 1] < sy.lo[1] - reach || p[i + 1] > sy.hi[1] + reach || p[i + 2] < sy.lo[2] - reach || p[i + 2] > sy.hi[2] + reach) continue;
                    const d = pointToSolid(sy, p[i], p[i + 1], p[i + 2]);
                    if (d < best) best = d;
                    if (best <= 0) return 0;
                }
            }
        }
    }
    // Two edges can pass closer than any vertex comes to the other part (planks crossing).
    if (best > 0) best = Math.min(best, edgeDistance(a, b, Math.min(best, reach)));
    return best <= reach ? best : Infinity;
}

/** The smallest distance between an edge of one part and an edge of the other, among edges within `reach` of the other's solid. */
function edgeDistance(a: FitPart, b: FitPart, reach: number): number {
    let best = Infinity;
    for (const sa of a.solids) {
        for (const sb of b.solids) {
            if (!boxesMeet(sa.lo, sa.hi, sb.lo, sb.hi, reach)) continue;
            const ea = edgesNear(sa, sb, reach), eb = edgesNear(sb, sa, reach);
            for (let i = 0; i < ea.length; i += 6) {
                for (let j = 0; j < eb.length; j += 6) best = Math.min(best, segmentDistance(ea, i, eb, j));
            }
        }
    }
    return best;
}

/** The edges of `s` (six numbers each, both ends) whose box comes within `reach` of `other`'s box. */
function edgesNear(s: FitSolid, other: FitSolid, reach: number): number[] {
    const p = s.positions, tri = s.shape.triangles, out: number[] = [], seen = new Set<number>(), n = p.length / 3;
    for (let t = 0; t < tri.length; t += 3) {
        for (let j = 0; j < 3; j++) {
            const lo = Math.min(tri[t + j], tri[t + (j + 1) % 3]), hi = Math.max(tri[t + j], tri[t + (j + 1) % 3]);
            // Each edge once, whether two triangles share it or it bounds an open shell.
            if (seen.has(lo * n + hi)) continue;
            seen.add(lo * n + hi);
            const u = lo * 3, v = hi * 3;
            let near = true;
            for (let k = 0; k < 3 && near; k++) near = Math.max(p[u + k], p[v + k]) >= other.lo[k] - reach && Math.min(p[u + k], p[v + k]) <= other.hi[k] + reach;
            if (near) out.push(p[u], p[u + 1], p[u + 2], p[v], p[v + 1], p[v + 2]);
        }
    }
    return out;
}

/** Distance between segments a[i..i+5] and b[j..j+5] (Ericson, Real-Time Collision Detection 5.1.9). */
function segmentDistance(a: number[], i: number, b: number[], j: number): number {
    const d1 = [a[i + 3] - a[i], a[i + 4] - a[i + 1], a[i + 5] - a[i + 2]], d2 = [b[j + 3] - b[j], b[j + 4] - b[j + 1], b[j + 5] - b[j + 2]];
    const r = [a[i] - b[j], a[i + 1] - b[j + 1], a[i + 2] - b[j + 2]];
    const dot = (x: number[], y: number[]): number => x[0] * y[0] + x[1] * y[1] + x[2] * y[2];
    const aa = dot(d1, d1), ee = dot(d2, d2), f = dot(d2, r), c = dot(d1, r), bb = dot(d1, d2);
    const clamp = (v: number): number => Math.min(1, Math.max(0, v));
    let s = 0, t = 0;
    if (aa <= 1e-24 && ee <= 1e-24) { s = 0; t = 0; }
    else if (aa <= 1e-24) t = clamp(f / ee);
    else if (ee <= 1e-24) s = clamp(-c / aa);
    else {
        const denom = aa * ee - bb * bb;
        s = denom > 1e-24 ? clamp((bb * f - c * ee) / denom) : 0;
        t = (bb * s + f) / ee;
        if (t < 0) { t = 0; s = clamp(-c / aa); } else if (t > 1) { t = 1; s = clamp((bb - c) / aa); }
    }
    return Math.hypot(r[0] + d1[0] * s - d2[0] * t, r[1] + d1[1] * s - d2[1] * t, r[2] + d1[2] * s - d2[2] * t);
}

function pointToSolid(s: FitSolid, x: number, y: number, z: number): number {
    const f = s.faces;
    if (s.shape.convex) {
        // Inside every face's half-space is inside; outside, the nearest face is found below.
        let out = -Infinity;
        for (let o = 0; o < f.length; o += FACE_STRIDE) if (f[o + 4] > 0) out = Math.max(out, f[o] * x + f[o + 1] * y + f[o + 2] * z - f[o + 3]);
        if (out <= 0) return 0;
    } else if (solidContains(s, x, y, z)) return 0;
    const p = s.positions, tri = s.shape.triangles;
    let best = Infinity;
    for (let t = 0; t < tri.length; t += 3) best = Math.min(best, pointTriangle(x, y, z, p, tri[t] * 3, tri[t + 1] * 3, tri[t + 2] * 3));
    return best;
}

/** Distance from a point to a triangle (Ericson, Real-Time Collision Detection 5.1.5). */
function pointTriangle(px: number, py: number, pz: number, p: Float64Array, a: number, b: number, c: number): number {
    const abx = p[b] - p[a], aby = p[b + 1] - p[a + 1], abz = p[b + 2] - p[a + 2];
    const acx = p[c] - p[a], acy = p[c + 1] - p[a + 1], acz = p[c + 2] - p[a + 2];
    const apx = px - p[a], apy = py - p[a + 1], apz = pz - p[a + 2];
    const d1 = abx * apx + aby * apy + abz * apz, d2 = acx * apx + acy * apy + acz * apz;
    const at = (u: number, v: number): number => Math.hypot(px - (p[a] + u * abx + v * acx), py - (p[a + 1] + u * aby + v * acy), pz - (p[a + 2] + u * abz + v * acz));
    if (d1 <= 0 && d2 <= 0) return at(0, 0);
    const bpx = px - p[b], bpy = py - p[b + 1], bpz = pz - p[b + 2];
    const d3 = abx * bpx + aby * bpy + abz * bpz, d4 = acx * bpx + acy * bpy + acz * bpz;
    if (d3 >= 0 && d4 <= d3) return at(1, 0);
    const vc = d1 * d4 - d3 * d2;
    if (vc <= 0 && d1 >= 0 && d3 <= 0) return at(d1 / (d1 - d3), 0);
    const cpx = px - p[c], cpy = py - p[c + 1], cpz = pz - p[c + 2];
    const d5 = abx * cpx + aby * cpy + abz * cpz, d6 = acx * cpx + acy * cpy + acz * cpz;
    if (d6 >= 0 && d5 <= d6) return at(0, 1);
    const vb = d5 * d2 - d1 * d6;
    if (vb <= 0 && d2 >= 0 && d6 <= 0) return at(0, d2 / (d2 - d6));
    const va = d3 * d6 - d5 * d4;
    if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) { const w = (d4 - d3) / ((d4 - d3) + (d5 - d6)); return at(1 - w, w); }
    const denom = 1 / (va + vb + vc);
    return at(vb * denom, vc * denom);
}
